<template>
  <base-container>
    <header-back-button title="Accueil" />
    <div class="bg-white px-4">
      <product-search-bar replace-route />
    </div>
    <div class="p-6 flex flex-col gap-3">
      <h1 class="text-black">
        {{ product.name }} <span class="text-gray-500 font-content">{{ product.code }}</span>
      </h1>
      <product-availability-rate-card
        class="mb-2"
        :request-token="String($route.query.requestToken)"
        :product-id="Number(product.id)"
        :pharma-m-l="pharmaMl"
        @availability-loaded="handleAvailabilityData"
      />
      <product-availability-statistics-card
        :product-id="Number($route.params.productId)"
        :request-token="$route.query.requestToken as string"
        @pharma-m-l-updated="value => pharmaMl=value"
      />
      <div class="grid grid-cols-2 gap-4">
        <distributor-availability-map
          :product="product"
          :data="groupWholesalers?.details ?? null"
          :is-available="groupWholesalers?.is_available"
        />
        <healthcenter-group-availability-map
          :product="product"
          :data="healthcenterGroups?.details ?? null"
          :is-available="healthcenterGroups?.is_available"
          :message="healthcenterGroups?.messages?.length ? healthcenterGroups?.messages[0] : undefined"
        />
        <direct-product-availability-card
          v-if="directAvailability"
          :data="directAvailability"
        />
        <market-place-availability
          :medidestock="medidestock"
          :le-comptoir-des-pharmacies="leComptoirDesPharmacies"
        />
      </div>
      <disruption-product-availability
        :filters="{productIds: [Number($route.params.productId)]}"
        :request-token="$route.query.requestToken as string"
      />
    </div>
  </base-container>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import HeaderBackButton from "@/core/components/header-back-button/HeaderBackButton.vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import ProductSearchBar from "@/disruption-logistics/components/product-search-bar/ProductSearchBar.vue";
import ProductAvailabilityRateCard, {
    AvailabilityState
} from "@/disruption-logistics/components/product-availability-card/ProductAvailabilityRateCard.vue";
import {AVAILABILITY_PROVIDERS} from "@/core/http/resources/DisruptionLogisticsResource";
import HealthcenterGroupAvailabilityMap
    from "@/disruption-logistics/components/maps/healthcenter-groups/HealthcenterGroupAvailabilityMap.vue";
import DistributorAvailabilityMap
    from "@/disruption-logistics/components/maps/distributors/DistributorAvailabilityMap.vue";
import {
    AvailabilityProviderData,
    DepositaryDetails,
    HealthcenterGroup,
    HealthcenterGroupDistributor, PharmaML
} from "@/disruption-logistics/interfaces/availability-providers";
import ProductAvailabilityStatisticsCard
    from "@/disruption-logistics/components/availability-statistics/ProductAvailabilityStatisticsCard.vue";
import MarketPlaceAvailability from "@/disruption-logistics/components/maps/distributors/MarketPlaceAvailability.vue";
import {useRoute} from "vue-router";
import DirectProductAvailabilityCard
    from "@/disruption-logistics/components/product-availability-card/DirectProductAvailabilityCard.vue";
import DisruptionProductAvailability from "@/disruption-logistics/views/DisruptionProductAvailability.vue";

const route = useRoute();
const healthcenterGroups = ref<null | HealthcenterGroup>(null);
const groupWholesalers = ref<null | HealthcenterGroupDistributor>(null);
const medidestock = ref<null | any>(null);
const leComptoirDesPharmacies = ref<null | any>(null);
const directAvailability = ref<AvailabilityState<DepositaryDetails>>();
const pharmaMl = ref<PharmaML>();

const product = computed(() => {
    return {
        id: Number(route.params.productId),
        name: route.query.productName as string,
        code: route.query.productCode as string
    };
});

watch(() => route.params.productId, () => {
    healthcenterGroups.value = null;
    groupWholesalers.value = null;
}, {immediate: true});

/**
 * Store loaded data in the component
 */
const handleAvailabilityData = ({provider, data}: { provider: string; data: AvailabilityProviderData }) => {
    if (provider === AVAILABILITY_PROVIDERS.healthcenterGroups) {
        healthcenterGroups.value = data as HealthcenterGroup;
    }

    if (provider === AVAILABILITY_PROVIDERS.healthcenterGroupsDistributor) {
        groupWholesalers.value = data as HealthcenterGroupDistributor;
    }

    if (provider === AVAILABILITY_PROVIDERS.medidestock) {
        medidestock.value = data;
    }

    if (provider === AVAILABILITY_PROVIDERS.leComptoirDesPharmacies) {
        leComptoirDesPharmacies.value = data;
    }

    if (provider === AVAILABILITY_PROVIDERS.direct) {
        directAvailability.value = data as AvailabilityState<DepositaryDetails>;
    }
};

</script>
